import { useQuery } from '@tanstack/react-query';
import qs from 'qs';

import { getHttpClient, useHttpClient } from '@bonusly/http-client';
import { getQueryClient } from '@bonusly/query-client';

import { GroupType } from '../schemas/groups';
import { User } from '../schemas/users';
import { listOf } from '../schemas/utils';

enum QueryKey {
	SearchWithRules = 'users:rules-search',
	Metadata = 'users:metadata',
}

export const userSearchQueryKey = (params?: Record<string, any>) => [QueryKey.SearchWithRules, params].filter(Boolean);
export const usersMetadataQueryKey = (params?: Record<string, any>) => [QueryKey.Metadata, params].filter(Boolean);

export const userSearchPath = (prefix = '') => `${prefix}/user_management/users/search`;
export const usersMetadataPath = (prefix = '') => `${prefix}/users/metadata/user_count`;

export type RuleCondition = {
	attr: string;
	value: string;
};

export type UserAPIData = {
	id: string;
	displayName: string;
	profilePic: string;
	username: string;
	companyAdmin?: boolean;
	otpRequiredForLogin?: boolean;
};

type SearchUsersParams = {
	search?: string;
	conditions?: RuleCondition[];
	operator?: 'and' | 'or';
	excludeConditions?: RuleCondition[];
	limit?: number;
	offset?: number;
};

const createSearchUsersFetcher =
	({ search, conditions = [], operator = 'or', excludeConditions = [], limit, offset }: SearchUsersParams = {}) =>
	async () => {
		const http = await getHttpClient();
		const body: {
			search?: string;
			or_conditions?: RuleCondition[];
			and_conditions?: RuleCondition[];
			exclude_conditions?: RuleCondition[];
			limit?: number;
			offset?: number;
		} = {};

		if (search) {
			body.search = search;
		}

		if (conditions.length && operator === 'or') {
			body.or_conditions = conditions;
		}

		if (conditions.length && operator === 'and') {
			body.and_conditions = conditions;
		}

		if (excludeConditions.length) {
			body.exclude_conditions = excludeConditions;
		}

		if (typeof limit === 'number') {
			body.limit = limit;
		}

		if (typeof offset === 'number' && offset > 0) {
			body.offset = offset;
		}

		const res = await http.post<UserAPIData[]>(userSearchPath(), body, {
			headers: {
				'Key-Inflection': 'camel',
			},
		});

		return listOf(User).parse(res.data);
	};

export const loadSearchUsers = async (params: SearchUsersParams = {}) => {
	const queryClient = await getQueryClient();

	return queryClient.fetchQuery({
		queryKey: userSearchQueryKey(params),
		queryFn: createSearchUsersFetcher(params),
	});
};

export const useSearchUsers = (params: SearchUsersParams = {}) => {
	return useQuery({
		queryKey: userSearchQueryKey(params),
		queryFn: createSearchUsersFetcher(params),
	});
};

type UserCountMetadataParams = {
	status?: 'active' | 'inactive';
	mode?: 'receiver' | 'giver';
	conditions?: RuleCondition[];
	operator?: GroupType;
	// NOTE: OR operator is used for exclude conditions
	excludeConditions?: RuleCondition[];
};

type UserCountMetadataAPIData = {
	totalCount: number;
	scopedCount: number;
};

export const useUserCountMetadata = (params: UserCountMetadataParams = {}) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: usersMetadataQueryKey(params),
		queryFn: async () => {
			const { excludeConditions, ...rest } = params;
			const requestParams: Record<string, unknown> = { ...rest, exclude_conditions: excludeConditions };

			const res = await http.get<UserCountMetadataAPIData>(usersMetadataPath(), {
				params: requestParams,
				headers: {
					'Key-Inflection': 'camel',
				},
				paramsSerializer: (serializedParams: Record<string, unknown>) => {
					return qs.stringify(serializedParams, { arrayFormat: 'indices' });
				},
			});

			return res.data;
		},
	});
};
